/**
 * CardInspectService — long-press a card to read its full ability text.
 *
 * Regular cards show only the ability keywords: the explanatory line
 * (`.minion-ability-desc`) is hidden by CSS. Pressing and holding a card
 * (hand, battlefield or combat minion) shows a large, NON-INTERACTIVE
 * overlay — after LONG_PRESS_MS (500 ms) for a mouse hold, or
 * LONG_PRESS_TOUCH_MS (600 ms of (near-)stillness) for a touch hold (see the
 * long-press vs drag section below). The overlay is a centered group of
 * [KEYWORD PANEL][ENLARGED CARD]: the magnified card sits on the right and a
 * panel to its LEFT lists EVERY keyword of the card with its full
 * explanation (so the text never overflows the card itself). The overlay
 * exists only while the press is active and intercepts nothing
 * (pointer-events: none on the whole overlay), so it cannot steal the
 * press, a tap, or any other interaction.
 *
 * No card code is duplicated: the magnified card is a live DOM clone of the
 * pressed element (cloneNode(true)) — same element classes, same markup,
 * same styling. The card's single source of truth remains MinionCard.ts plus
 * the "Minion Card" CSS section in index.html; this service only clones,
 * positions and scales the existing element. The keyword panel is built from
 * the pressed card's OWN DOM (`.minion-abilities` keyword names) + the
 * shared GAME_ABILITY_DEFINITIONS (name → explanation), so no ability text
 * is duplicated here either.
 *
 * Implemented with document-level event delegation, so cards re-rendered on
 * every state change need no re-wiring. Presses that move beyond the
 * movement tolerance cancel the overlay so normal drags (DragAndDropService)
 * keep working, and an HTML5 dragstart hides it as well. dispose() must be
 * called when the app shell is torn down — the document-level listeners must
 * not accumulate across shell restarts (same contract as DragAndDropService).
 *
 * Long-press vs drag (the mobile collision the user sees as "long-press
 * picks the card up" / "preview flashes while dragging"):
 *  - TOUCH preview = a press that stays (within TOUCH_DRAG_THRESHOLD of the
 *    start point) for LONG_PRESS_TOUCH_MS (600 ms of stillness). A mouse
 *    press keeps the shorter 500 ms hold (mouse pointers do not jitter).
 *  - The TOUCH movement tolerance IS DragAndDropService's
 *    TOUCH_DRAG_THRESHOLD (imported, so the two can never diverge):
 *    PRE-FIRE movement (before the 600 ms mark) starts a touch drag and
 *    cancels the pending preview on the same touchmove event (a
 *    jittering/creeping hold never flashes the preview over an active
 *    drag). POST-FIRE movement (the preview is already up) does NOT start
 *    a drag and does NOT hide the preview: a completed long-press is a
 *    "read" gesture and owns the touch sequence until release. The
 *    no-drag half of that is DragAndDropService's long-press lock — armed
 *    at the SAME LONG_PRESS_TOUCH_MS mark (also imported from there, so
 *    the two can never diverge): a press that was still for the full
 *    duration can never become a drag, so a finger drifting over the
 *    read-only preview can never pick the card up (the "long-press sells
 *    my card" bug).
 *
 * Native long-press DRAG disarming (the mobile "buzz + card picked up,
 * preview never shows" bug):
 *  - A `draggable="true"` card (hand / battlefield) that is TOUCH-held for
 *    ~500 ms makes Chromium mobile (Android Chrome, Capacitor WebView) start
 *    its OWN native HTML5 drag — with a haptic "buzz" and the card's drag
 *    image, so the card appears "picked up" by the BROWSER (the DnD service's
 *    HTML5 dragstart handler runs, and the card can even be played/sold/
 *    repositioned wherever the native drag is released). The browser then
 *    aborts the touch sequence with touchcancel, which kills this service's
 *    600 ms preview timer — the large preview therefore NEVER shows. (CSS
 *    `user-select`/`-webkit-touch-callout` already suppress the
 *    text-selection variant; the `draggable` attribute is what remains.)
 *  - Fix: for the duration of a touch press the card's `draggable` attribute
 *    is removed (_disarmNativeTouchDrag) and restored on touchend/
 *    touchcancel/dispose. The game's own touch drag is pure JS (touch events,
 *    never the HTML5 drag API), so it is unaffected; desktop mouse drags keep
 *    the attribute (only touch presses disarm).
 *
 * Touch/mobile collision fix: when a long-press COMPLETES on a touch device,
 * releasing the finger makes the browser dispatch SYNTHETIC mouse events
 * (mousedown/mouseup/click — and dblclick when a quick tap follows within
 * the double-tap window). Those synthetic clicks landed on the card and
 * fired the card's actions (double-tap-to-play, double-tap-to-upgrade),
 * colliding with the preview gesture. Two layers stop that:
 *  1. the touchend that ends a completed long-press is CANCELED
 *     (e.preventDefault() — per the Touch Events spec, a canceled touchend
 *     suppresses the compatibility mouse events);
 *  2. backstop (browsers vary in honoring (1)): for a short window after
 *     the long-press fired, click/dblclick events whose target is the
 *     long-pressed card itself are intercepted in the capture phase and
 *     swallowed. Only that exact card is affected, so taps on buttons and
 *     on other cards are never eaten. Synthetic mousemoves/mousedowns in the
 *     window are ignored so they cannot re-arm a new press.
 */

import { TOUCH_DRAG_THRESHOLD, LONG_PRESS_TOUCH_MS } from './DragAndDropService';
import { GAME_ABILITY_DEFINITIONS } from '../../services/GameAbilities';

export class CardInspectService {
  /** Hold duration before the inspect overlay appears for MOUSE presses (desktop). */
  private static readonly LONG_PRESS_MS = 500;
  /**
   * Hold duration before the inspect overlay appears for TOUCH presses.
   * Imported (not redefined) from DragAndDropService: the drag service arms
   * its long-press DRAG LOCK at this exact moment — a press still for this
   * long is a "read" gesture that can never become a drag (see the class
   * doc). If the two constants ever diverged, a completed long-press could
   * still turn into an accidental drag (the "long-press sells my card"
   * bug).
   */
  // TOUCH long-press duration = LONG_PRESS_TOUCH_MS (shared with DragAndDropService).
  /** Pointer movement that cancels a MOUSE press (keeps drags working). Touch presses use the shared TOUCH_DRAG_THRESHOLD. */
  private static readonly MOVE_TOLERANCE_PX = 10;
  /** The magnified card should reach ~72 % of the viewport height... */
  private static readonly TARGET_HEIGHT_FRACTION = 0.72;
  /** ...and at most ~40 % of the viewport width (landscape-first mobile). */
  private static readonly TARGET_WIDTH_FRACTION = 0.4;
  /** Hard cap so desktop cards don't become absurdly large. */
  private static readonly MAX_SCALE = 3.5;
  /**
   * How long (ms) after a long-press fired synthetic click/dblclick events
   * on the pressed card are swallowed (backstop for browsers that do not
   * suppress them when the touchend is canceled). Must cover the browser's
   * double-tap window so a "long-press + quick tap" cannot form a dblclick.
   */
  private static readonly CLICK_SUPPRESS_WINDOW_MS = 400;

  private _overlay: HTMLElement | null = null;
  private _timer: number | null = null;
  private _pressedCard: HTMLElement | null = null;
  /** Whether the ACTIVE press started from a touch. The synthetic-click suppression only applies to touch long-presses (a real mouse release is a genuine click). */
  private _pressIsTouch = false;
  /** True while a completed long-press overlay is on screen (movement must dismiss it, same as for the pre-fire timer). */
  private _longPressActive = false;
  /**
   * The card a COMPLETED long-press is displayed for (kept after _show, when
   * _pressedCard is cleared, so isTouchPressActive keeps working while the
   * preview is up). Null when no preview is on screen.
   */
  private _longPressCard: HTMLElement | null = null;
  private _startX = 0;
  private _startY = 0;
  private _installed = false;
  /**
   * Active synthetic-click suppression: the card a long-press completed on
   * and the timestamp (ms, Date.now) until which clicks/dblclicks on that
   * exact card are swallowed. Null = no suppression.
   */
  private _suppress: { card: HTMLElement; until: number } | null = null;
  /**
   * Optional listener fired when a long-press preview SHOWS (source = the
   * pressed card element). Set via setOnInspect (null clears it). Used by
   * the in-game tutorial to detect the long-press tip's completion — the
   * service itself stays agnostic of WHY a listener cares.
   */
  private _onInspect: ((card: HTMLElement) => void) | null = null;
  /**
   * The card whose `draggable` attribute was REMOVED for the active touch
   * press (native long-press drag disarming — see _disarmNativeTouchDrag),
   * plus the attribute's previous value (restored exactly: the app uses
   * "true" or no attribute, but the service stays general). Null = nothing
   * disarmed.
   */
  private _disarmedCard: HTMLElement | null = null;
  private _disarmedDraggablePrev: string | null = null;

  constructor() {
    this._install();
  }

  /**
   * Sets (or clears, with null) the listener that fires when a long-press
   * preview appears. Safe to call at any time; no-ops when no listener is
   * set, so production callsites pay nothing.
   */
  setOnInspect(listener: ((card: HTMLElement) => void) | null): void {
    this._onInspect = listener;
  }

  /**
   * True while an active TOUCH press is on `element` — either a pending
   * touch press (finger down, the 600 ms preview not yet fired:
   * `_pressedCard`
   * is set) or a COMPLETED long-press with the preview on screen
   * (`_longPressCard` is set; `_pressedCard` was cleared by _show).
   *
   * Why: Chromium mobile fires `contextmenu` ~500 ms into a touch long-
   * press — historically the battlefield minion's contextmenu listener was
   * the "right-click to sell" path, so an unguarded mobile long-press SOLD
   * the minion while the touch sequence kept running (the preview still
   * appeared later, but the card was already gone). Right-click-to-sell has
   * since been REMOVED (the listener is gone with it), but the guard stays:
   * it still tells a mobile long-press (a preview gesture) apart from a
   * genuine mouse click.
   */
  isTouchPressActive(element: HTMLElement): boolean {
    if (!this._pressIsTouch) return false;
    const pressCard = this._pressedCard ?? this._longPressCard;
    if (!pressCard) return false;
    return pressCard === element
      || pressCard.contains(element)
      || element.contains(pressCard);
  }

  /**
   * Removes all document-level listeners and the overlay. Safe to call
   * multiple times. main.ts creates a fresh service for every app shell
   * (login/re-login, restart after game over) — without dispose() the old
   * shells' listeners would accumulate (same hazard as the sell drop zone).
   */
  dispose(): void {
    if (this._installed) {
      document.removeEventListener('touchstart', this._onTouchStart);
      document.removeEventListener('touchmove', this._onTouchMove);
      document.removeEventListener('touchend', this._onTouchEnd);
      document.removeEventListener('touchcancel', this._onTouchEnd);
      document.removeEventListener('mousedown', this._onMouseDown);
      document.removeEventListener('mousemove', this._onMouseMove);
      document.removeEventListener('mouseup', this._hide);
      document.removeEventListener('dragstart', this._hide, true);
      document.removeEventListener('click', this._onClickCapture, true);
      document.removeEventListener('dblclick', this._onClickCapture, true);
      this._installed = false;
    }
    this._restoreDisarmedCard();
    this._hide();
  }

  private _install(): void {
    if (this._installed || typeof document === 'undefined') return;
    this._installed = true;

    document.addEventListener('touchstart', this._onTouchStart, { passive: true });
    document.addEventListener('touchmove', this._onTouchMove, { passive: true });
    document.addEventListener('touchend', this._onTouchEnd);
    document.addEventListener('touchcancel', this._onTouchEnd);
    // Left button only — right-click does nothing (the contextmenu sell was removed).
    document.addEventListener('mousedown', this._onMouseDown);
    document.addEventListener('mousemove', this._onMouseMove);
    document.addEventListener('mouseup', this._hide);
    // Capture phase: if an HTML5 drag starts, the inspect view must vanish
    // even if the pointer has barely moved.
    document.addEventListener('dragstart', this._hide, true);
    // Capture phase: swallow the SYNTHETIC click/dblclick a completed touch
    // long-press produces on release (see the class doc for the collision).
    document.addEventListener('click', this._onClickCapture, true);
    document.addEventListener('dblclick', this._onClickCapture, true);
  }

  // ─── Press tracking ─────────────────────────────────────────────────────

  /** (Re)starts press tracking for the given pointer position/card. */
  private _beginPress(x: number, y: number, card: HTMLElement | null, isTouch: boolean): void {
    this._hide(); // any previous press/overlay is replaced by this one
    // The previous press (if any) is fully over — its synthetic-click phase
    // already happened before this new touch began, so drop the window.
    this._suppress = null;
    // A new press supersedes any still-open disarmed card (multi-touch
    // edge): restore the previous one before disarming the new card.
    this._restoreDisarmedCard();
    this._pressedCard = card;
    this._pressIsTouch = isTouch;
    this._startX = x;
    this._startY = y;
    if (!card) return;
    // Touch press on a card: disarm the browser's native long-press drag for
    // the duration of this press (it would "buzz", pick the card up and kill
    // the 600 ms preview timer via touchcancel — see
    // _disarmNativeTouchDrag).
    if (isTouch) this._disarmNativeTouchDrag(card);
    // Touch holds must be a sustained 600 ms (a jittering finger is not a
    // preview intent); mouse holds stay at the short desktop duration.
    const holdMs = isTouch
      ? LONG_PRESS_TOUCH_MS
      : CardInspectService.LONG_PRESS_MS;
    this._timer = window.setTimeout(() => {
      this._timer = null;
      this._show(card);
    }, holdMs);
  }

  private _cancelTimer(): void {
    if (this._timer !== null) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  /** True once the pointer has moved far enough to count as a drag. */
  private _isMovement(x: number, y: number): boolean {
    // Touch presses use the EXACT threshold DragAndDropService starts a
    // touch drag at — so a movement that begins a drag also cancels the
    // preview (timer or shown overlay) on the same touchmove event, and
    // sub-threshold jitter during a 600 ms hold never kills the preview.
    // Mouse presses keep the desktop tolerance (dragstart hides the
    // overlay on desktop drags regardless).
    const tolerance = this._pressIsTouch
      ? TOUCH_DRAG_THRESHOLD
      : CardInspectService.MOVE_TOLERANCE_PX;
    return Math.hypot(x - this._startX, y - this._startY) > tolerance;
  }

  /**
   * Resolves the card a press started on, or null. Presses on the card's
   * interactive chrome (Play / Sell / Upgrade buttons) are ignored so those
   * clicks are never swallowed.
   */
  private _findCard(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null;
    if (target.closest('button')) return null;
    return target.closest('.hand-card, .battlefield-minion, .combat-minion');
  }

  /**
   * Removes `card`'s `draggable` attribute for the duration of the active
   * touch press so the browser's native long-press drag (a ~500 ms hold on a
   * draggable element in Chromium mobile: haptic "buzz" + the card's drag
   * image = "the card is picked up", followed by a touchcancel that kills
   * the 600 ms preview timer) can never start. Restored by
   * _restoreDisarmedCard when the touch sequence ends (touchend or
   * touchcancel), on a superseding press, or on dispose.
   *
   * Safe for this app: the mobile drag is pure JS (touch events — see
   * DragAndDropService's touch implementation, which never uses the HTML5
   * drag API), and desktop HTML5 drags start from MOUSE events, which never
   * disarm (the attribute is only touched by touch presses). Cards without a
   * draggable attribute (combat minions) and cards already off are left alone.
   */
  private _disarmNativeTouchDrag(card: HTMLElement): void {
    const prev = card.getAttribute('draggable');
    if (prev === null || prev === 'false') {
      // Nothing to disarm (attribute absent or already off).
      this._disarmedCard = null;
      this._disarmedDraggablePrev = null;
      return;
    }
    card.setAttribute('draggable', 'false');
    this._disarmedCard = card;
    this._disarmedDraggablePrev = prev;
  }

  /** Restores the `draggable` attribute removed by _disarmNativeTouchDrag. No-op when nothing was disarmed. */
  private _restoreDisarmedCard(): void {
    if (!this._disarmedCard) return;
    const card = this._disarmedCard;
    const prev = this._disarmedDraggablePrev;
    this._disarmedCard = null;
    this._disarmedDraggablePrev = null;
    if (prev === null) card.removeAttribute('draggable');
    else card.setAttribute('draggable', prev);
  }

  // ─── Event handlers ─────────────────────────────────────────────────────

  private _onTouchStart = (e: TouchEvent): void => {
    const t = e.changedTouches[0];
    this._beginPress(t.clientX, t.clientY, this._findCard(e.target), true);
  };

  private _onTouchMove = (e: TouchEvent): void => {
    if (!this._pressedCard && !this._longPressActive) return;
    const t = e.changedTouches[0];
    if (!this._isMovement(t.clientX, t.clientY)) return;
    if (this._longPressActive) {
      // A COMPLETED long-press is a "read" gesture: the preview stays up
      // until release, and DragAndDropService's long-press lock (armed at
      // the same 600 ms mark) guarantees this touch sequence can no longer
      // start a drag — a finger drifting over the read-only preview must
      // never pick the card up (the "long-press sells my card" bug).
      return;
    }
    // PRE-FIRE movement: the press is becoming a drag — cancel the
    // long-press entirely (and any armed click suppression: a drag release
    // is not a long-press release, so its synthetic events must stay
    // intact).
    this._cancelTimer();
    this._hide();
    this._suppress = null;
    this._pressedCard = null;
  };

  private _onTouchEnd = (e: TouchEvent): void => {
    // A long-press completed during this touch: CANCEL the touchend so the
    // browser does not dispatch the synthetic mousedown/mouseup/click (and
    // follow-up dblclick) that would collide with the card's tap actions.
    // (touchcancel is not cancelable — preventDefault is a harmless no-op.)
    const longPressWasActive = this._suppress !== null;
    if (longPressWasActive) {
      e.preventDefault();
      if (this._suppress) {
        // Refresh the backstop window from the RELEASE moment: the synthetic
        // click fires right after touchend, and a quick second tap must still
        // be covered (it is the dblclick collision).
        this._suppress = { card: this._suppress.card, until: Date.now() + CardInspectService.CLICK_SUPPRESS_WINDOW_MS };
      }
    }
    this._cancelTimer();
    this._restoreDisarmedCard();
    this._hide();
    this._pressedCard = null;
  };

  /**
   * Capture-phase backstop: swallows the synthetic click/dblclick a completed
   * touch long-press produces on the very card that was held (see class doc).
   */
  private _onClickCapture = (e: Event): void => {
    if (this._isClickSuppressed(e)) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  private _onMouseDown = (e: MouseEvent): void => {
    if (e.button !== 0) return;
    // A synthetic mousedown from a just-released long-press must not re-arm
    // a new 500ms press (its synthetic mouseup would fire anyway, but
    // tracking a dead press is pointless and racy).
    if (this._isClickSuppressed(e)) return;
    this._beginPress(e.clientX, e.clientY, this._findCard(e.target), false);
  };

  private _onMouseMove = (e: MouseEvent): void => {
    if (!this._pressedCard && !this._longPressActive) return;
    if (this._isMovement(e.clientX, e.clientY)) {
      this._cancelTimer();
      this._hide();
      this._suppress = null;
      this._pressedCard = null;
    }
  };

  // ─── Overlay ────────────────────────────────────────────────────────────

  /** Starts (or refreshes) the synthetic-click suppression window for a card. */
  private _armClickSuppression(card: HTMLElement): void {
    this._suppress = { card, until: Date.now() + CardInspectService.CLICK_SUPPRESS_WINDOW_MS };
  }

  /**
   * True while the synthetic-click suppression window is active for `card`
   * (the card a long-press just completed on). Used to swallow the browser's
   * synthetic click/dblclick after a touch long-press release.
   */
  private _isClickSuppressed(e: Event): boolean {
    if (!this._suppress || Date.now() > this._suppress.until) return false;
    if (!(e.target instanceof Element)) return false;
    return this._suppress.card === e.target || this._suppress.card.contains(e.target);
  }

  /**
   * Shows the magnified clone of `source`, centered in the viewport and
   * scaled to fill most of it (landscape mobile first). The clone keeps
   * the real card layout (corner stat badges, class badge straddling the
   * top border) — its height is pinned to the card's actual footprint and
   * extended below for the revealed ability descriptions, so the scale is
   * computed from the clone's ACTUAL laid-out size (card + description
   * area).
   */
  private _show(source: HTMLElement): void {
    this._hide();
    if (typeof document === 'undefined') return;
    // The overlay is live: movement (beyond the tolerance) must dismiss it
    // so drags keep working — _hide() in the first line above cleared
    // _pressedCard, so the move handlers track the active long-press via
    // this flag.
    this._longPressActive = true;
    this._longPressCard = source;

    // A long-press has completed: from now until the release (and briefly
    // after it) the browser may fire synthetic click/dblclick events on this
    // card — arm the suppression window (refreshed on touchend). Touch only:
    // a real mouse long-press release is a genuine click and keeps its
    // (no-op on cards) desktop behavior.
    if (this._pressIsTouch) {
      this._armClickSuppression(source);
    }

    const overlay = document.createElement('div');
    overlay.className = 'card-inspect-overlay';
    // Inline as well as via CSS — the overlay must be strictly
    // non-interactive regardless of stylesheet state.
    overlay.style.pointerEvents = 'none';

    // The KEYWORDS PANEL — every keyword of this card + its full
    // explanation — sits to the LEFT of the enlarged card (the overlay CSS
    // lays out [panel][scalebox] as a centered flex group). Cards without
    // abilities get no panel (just the centered card).
    const panel = this._buildKeywordPanel(source);
    if (panel) overlay.appendChild(panel);

    const clone = source.cloneNode(true) as HTMLElement;
    clone.classList.add('card-inspect-card');
    clone.removeAttribute('draggable');
    clone.style.pointerEvents = 'none';
    // The clone keeps the REAL card layout (badges, name, hidden desc line —
    // the desc stays hidden on the clone; the panel carries the text).

    // The clone lives in a scalebox pre-sized to the card's VISUAL size
    // (w*scale × h*scale, below), so the flex row sees the true footprint
    // and the panel/card gap stays real at any viewport. transform-origin
    // top-left (CSS) lines the scaled clone up with the box's top-left.
    const box = document.createElement('div');
    box.className = 'card-inspect-scalebox';
    box.appendChild(clone);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
    this._overlay = overlay;

    // The preview is on screen — notify the optional listener (tutorial).
    this._onInspect?.(source);

    const w = source.offsetWidth || source.clientWidth || 100;
    const h = source.offsetHeight || source.clientHeight || 140;
    const scale = Math.min(
      (window.innerHeight * CardInspectService.TARGET_HEIGHT_FRACTION) / h,
      (window.innerWidth * CardInspectService.TARGET_WIDTH_FRACTION) / w,
      CardInspectService.MAX_SCALE,
    );
    box.style.width = `${w * scale}px`;
    box.style.height = `${h * scale}px`;
    clone.style.transform = `scale(${scale.toFixed(3)})`;
  }

  /**
   * Builds the keyword panel for the inspect overlay: a titled list with
   * one entry per keyword of the pressed card, each showing the keyword
   * name and its full explanation.
   *
   * The keyword NAMES come from the card's own DOM (`.minion-abilities` —
   * "★ A, B, C"), so the panel always matches what the card actually shows;
   * the EXPLANATIONS come from the shared GAME_ABILITY_DEFINITIONS (the same
   * source MinionCard's hidden `.minion-ability-desc` line is rendered
   * from), so no ability text is duplicated in this service. Cards without
   * a keyword line (or with only unknown keywords) still get a panel if at
   * least one keyword name exists; cards with no keywords get no panel.
   *
   * @returns The panel element, or null when the card has no keywords.
   */
  private _buildKeywordPanel(source: HTMLElement): HTMLElement | null {
    if (typeof document === 'undefined') return null;
    const namesEl = source.querySelector('.minion-abilities');
    if (!namesEl) return null;
    // "★ Fortress, Warband" → ["Fortress", "Warband"].
    const names = (namesEl.textContent ?? '')
      .replace(/^\s*★\s*/, '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (names.length === 0) return null;

    const panel = document.createElement('div');
    panel.className = 'card-inspect-panel';
    const title = document.createElement('div');
    title.className = 'card-inspect-panel-title';
    title.textContent = 'Keywords';
    panel.appendChild(title);

    for (const name of names) {
      const def = GAME_ABILITY_DEFINITIONS.find((d) => d.name === name);
      const item = document.createElement('div');
      item.className = 'card-inspect-kw';
      const nameSpan = document.createElement('span');
      nameSpan.className = 'card-inspect-kw-name';
      nameSpan.textContent = name;
      item.appendChild(nameSpan);
      if (def?.description) {
        const descSpan = document.createElement('span');
        descSpan.className = 'card-inspect-kw-desc';
        descSpan.textContent = def.description;
        item.appendChild(descSpan);
      }
      panel.appendChild(item);
    }
    return panel;
  }

  /** Removes the overlay and all press state. Safe to call any time. */
  private _hide = (): void => {
    this._cancelTimer();
    if (this._overlay) {
      this._overlay.remove();
      this._overlay = null;
    }
    this._longPressActive = false;
    this._longPressCard = null;
    this._pressedCard = null;
  };
}
